import { useState, useEffect, useCallback, useMemo, useRef, type DragEvent, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  Row,
  Col,
  Tabs,
  Select,
  DatePicker,
  Button,
  Space,
  Table,
  Tag,
  Popconfirm,
  App,
  InputNumber,
  Dropdown,
  Tooltip,
  Modal,
  Upload,
} from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import {
  TableOutlined,
  FilterOutlined,
  ReloadOutlined,
  DownloadOutlined,
  DownOutlined,
  SearchOutlined,
  SettingOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  UploadOutlined,
  ClearOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { formatDisplayDateTime, formatOnChainLabel, isOnChained } from '../../utils/format'
import { formatDocumentStatus } from '../../utils/documentStatus'
import { dataSourcesApi, dataSourceFilterSchemesApi, importLogsApi, unwrapKingdeeTabRows } from '../../services/api'
import Permission from '../../components/Permission'
import AdvancedQueryModal, { emptyAdvancedQuery } from './AdvancedQueryModal'
import ExportFieldModal from './ExportFieldModal'
import ColumnSettingsModal from './ColumnSettingsModal'
import FilterMultiSelect from './FilterMultiSelect'
import OutstockDetailModal from '../AssetDataDetail/OutstockDetailModal'
import {
  loadVisibleColumnKeys,
  saveVisibleColumnKeys,
  loadColumnOrder,
  saveColumnOrder,
  reorderColumnKeys,
  normalizeColumnOrder,
} from './listColumns'
import type { AdvancedQueryPayload, FilterFormSnapshot, SavedFilterScheme } from './savedFilterSchemes'
import {
  saveDataListCache,
  parseCachedDayRange,
  getPendingRestoreCache,
  markListCachePendingRestore,
  clearListCachePendingRestore,
  type DataListCachePayload,
} from '../../utils/dataListCache'
import { saveSummaryFilters } from '../../utils/summaryFiltersCache'

const { RangePicker } = DatePicker

const statusTextMap: Record<string, string> = {
  available: '可用',
  void: '作废',
  written_off: '已核销',
  used: '已使用',
}

const statusColorMap: Record<string, string> = {
  available: 'green',
  void: 'red',
  written_off: 'orange',
  used: 'blue',
}

const LIST_SORTABLE_KEYS = new Set([
  'id',
  'name',
  'source',
  'type',
  'documentStatus',
  'onChain',
  'contractNo',
  'contractType',
  'customer',
  'settlementOrg',
  'productName',
  'rwaCategory',
  'priceTaxTotal',
  'receiptAmount',
  'partialReceipt',
  'invoicedAmount',
  'contractCreatedDate',
  'contractReceiptDate',
  'status',
  'createdTime',
])

const RWA_CATEGORY_OPTIONS = [
  { value: '货转', label: '货转' },
  { value: '物流', label: '物流' },
  { value: '自提', label: '自提' },
  { value: '信息', label: '信息' },
] as const

function normalizeStringList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String).map((s) => s.trim()).filter(Boolean)
  if (typeof v === 'string' && v.trim()) return [v.trim()]
  return []
}

/** 资产数据页「来源」筛选项：仅财务系统与 CSV/手工导入 */
const DATA_SOURCE_FILTER_SOURCES: string[] = ['财务系统', '外部导入']

function resolveInitialSourceFilter(cache: DataListCachePayload | null): string[] {
  if (cache) return normalizeStringList(cache.appliedSource)
  return []
}

/** 自定义表头：点击切换升/降序，拖拽手柄与排序分离，避免 Ant Design 受控 sortOrder 第三次点击卡死 */
function DataListColumnHeader({
  columnKey,
  label,
  sortable,
  sortColumnKey,
  sortOrder,
  onSort,
  showDragHandle,
  onDragStart,
  onDragEnd,
}: {
  columnKey: string
  label: ReactNode
  sortable: boolean
  sortColumnKey: string
  sortOrder: 'ascend' | 'descend'
  onSort: (key: string) => void
  showDragHandle?: boolean
  onDragStart?: (e: DragEvent<HTMLSpanElement>) => void
  onDragEnd?: () => void
}) {
  const active = sortable && sortColumnKey === columnKey
  const ascendOn = active && sortOrder === 'ascend'
  const descendOn = active && sortOrder === 'descend'

  const labelBlock = sortable ? (
    <button
      type="button"
      className="inline-flex items-center gap-1 min-w-0 border-0 bg-transparent p-0 text-inherit font-inherit cursor-pointer"
      onClick={() => onSort(columnKey)}
    >
      <span>{label}</span>
      <span className="inline-flex flex-col text-[10px] leading-[0.65] ml-0.5 shrink-0">
        <CaretUpOutlined className={ascendOn ? '!text-[#1677ff]' : '!text-black/25'} />
        <CaretDownOutlined className={descendOn ? '!text-[#1677ff]' : '!text-black/25'} />
      </span>
    </button>
  ) : (
    <span>{label}</span>
  )

  if (!showDragHandle) {
    return <span className="data-list-col-header flex justify-center items-center w-full min-w-0">{labelBlock}</span>
  }

  return (
    <span className="data-list-col-header flex justify-center items-center gap-1 w-full min-w-0">
      <span
        className="data-col-drag-handle text-gray-400 cursor-grab shrink-0 text-xs leading-none select-none px-0.5"
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        title="拖动调整列顺序"
      >
        ⋮⋮
      </span>
      {labelBlock}
    </span>
  )
}

/** 列表行（与后端字段兼容映射）；金额缺省为 null，表格显示「-」 */
export type DataRow = {
  key: string
  id: string
  name: string
  source: string
  type: string
  documentStatus: string
  /** 上链状态：接口 onChain「是/否」→ 已上链/未上链 */
  onChain: string
  contractNo: string
  contractType: string
  customer: string
  settlementOrg: string
  productName: string
  rwaCategory: string
  priceTaxTotal: number | null
  receiptAmount: number | null
  partialReceipt: string
  unreceivedAmount: number | null
  invoicedAmount: number | null
  contractCreatedDate: string
  contractReceiptDate: string
  value: number | null
  status: string
  createdTime: string
  /** POST /api/data-sources/contract-chain/upload */
  ordernumber: string
  bill_no: string
  /** 已上链时不展示「上链」操作 */
  onChainListed: boolean
  contractAttachment: string
  orderAttachment: string
  receiptAttachment: string
  logisticsAttachment: string
  invoiceAttachment: string
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '' || v === '-') return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  return n
}

function pickListOrdernumber(item: Record<string, unknown>): string {
  return String(
    item.ordernumber ??
      item.orderNumber ??
      item.order_number ??
      item.orderNo ??
      item.order_no ??
      item.FOrderNo ??
      item.forder_no ??
      '',
  ).trim()
}

function pickListBillNo(item: Record<string, unknown>): string {
  return String(
    item.bill_no ??
      item.billNo ??
      item.BillNo ??
      item.documentNo ??
      item.document_no ??
      item.billNumber ??
      item.bill_number ??
      item.billno ??
      item.contractNo ??
      item.contract_no ??
      item.contractNumber ??
      '',
  ).trim()
}

/** 空值统一为「-」（与接口 list 中空字段约定一致） */
function strOrDash(...vals: unknown[]): string {
  for (const x of vals) {
    if (x != null && x !== '') return String(x)
  }
  return '-'
}

function deriveUnreceived(item: Record<string, unknown>): number | null {
  const fromApi = numOrNull(item.unreceivedAmount ?? item.unreceived_amount)
  if (fromApi !== null) return Math.max(0, fromApi)
  const pt = numOrNull(item.priceTaxTotal ?? item.price_tax_total ?? item.totalAmount ?? item.amount)
  if (pt === null) return null
  const ra = numOrNull(item.receiptAmount ?? item.receipt_amount ?? item.receivedAmount) ?? 0
  return Math.max(0, pt - ra)
}

const AMOUNT_EPS = 0.01

function derivePartialReceipt(item: Record<string, unknown>): string {
  const fromApi = strOrDash(item.partialReceipt, item.partial_receipt)
  if (fromApi !== '-') return fromApi
  const pt = numOrNull(item.priceTaxTotal ?? item.price_tax_total ?? item.totalAmount ?? item.amount)
  if (pt === null || pt <= AMOUNT_EPS) return '-'
  const ra = numOrNull(item.receiptAmount ?? item.receipt_amount ?? item.receivedAmount) ?? 0
  if (ra <= AMOUNT_EPS || ra >= pt - AMOUNT_EPS) return '否'
  return '是'
}

function normalizeDataList(res: any): DataRow[] {
  const raw = res?.data
  const list = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.list)
      ? raw.list
      : Array.isArray(raw)
        ? raw
        : Array.isArray(res?.list)
          ? res.list
          : Array.isArray(res)
            ? res
            : []
  return list.map((item: any, i: number) => ({
    key: String(item.key ?? item.id ?? i + 1),
    id: item.id ?? item.data_id ?? '',
    name: item.name ?? item.data_name ?? '',
    source: item.source ?? '',
    type: item.type ?? '应收',
    documentStatus: (() => {
      const fromApi = strOrDash(item.documentStatus, item.document_status)
      if (fromApi !== '-') return fromApi
      const resolved = formatDocumentStatus(item as Record<string, unknown>)
      return resolved !== '-' ? resolved : '-'
    })(),
    onChain: (() => {
      const label = formatOnChainLabel(item.onChain ?? item.on_chain)
      if (label) return label
      if (
        isOnChained(item.onChain ?? item.on_chain) ||
        String(item.documentStatus ?? item.document_status ?? '').trim() === '已上链'
      ) {
        return '已上链'
      }
      return '-'
    })(),
    contractNo: strOrDash(item.contractNo, item.contract_no, item.contractNumber),
    contractType: strOrDash(item.contractType, item.contract_type),
    customer: strOrDash(item.customer, item.customerName),
    settlementOrg: strOrDash(item.settlementOrg, item.settlement_org, item.orgName),
    productName: strOrDash(item.productName, item.product_name, item.goodsName, item.goods_name),
    rwaCategory: strOrDash(item.rwaCategory, item.rwa_category),
    priceTaxTotal: numOrNull(item.priceTaxTotal ?? item.price_tax_total ?? item.totalAmount ?? item.amount),
    receiptAmount: numOrNull(item.receiptAmount ?? item.receipt_amount ?? item.receivedAmount),
    partialReceipt: derivePartialReceipt(item as Record<string, unknown>),
    unreceivedAmount: deriveUnreceived(item as Record<string, unknown>),
    invoicedAmount: numOrNull(item.invoicedAmount ?? item.invoiced_amount ?? item.invoiceAmount),
    contractCreatedDate: strOrDash(
      item.contractCreatedDate,
      item.contract_created_date,
      item.contractCreatedAt,
      item.contract_created_at
    ),
    contractReceiptDate: strOrDash(
      item.contractReceiptDate,
      item.contract_receipt_date,
      item.receiptDate,
      item.receipt_date
    ),
    value: numOrNull(item.value ?? item.amount),
    status: item.status ?? item.assetStatus ?? 'available',
    createdTime: strOrDash(
      item.createdTime,
      item.created_at,
      item.createdDate,
      item.create_time,
      item.created_date
    ),
    ordernumber: pickListOrdernumber(item as Record<string, unknown>),
    bill_no: pickListBillNo(item as Record<string, unknown>),
    onChainListed:
      isOnChained(item.onChain ?? item.on_chain) ||
      String(item.documentStatus ?? item.document_status ?? '').trim() === '已上链',
    contractAttachment: strOrDash(item.contractAttachment, item.contract_attachment),
    orderAttachment: strOrDash(item.orderAttachment, item.order_attachment),
    receiptAttachment: strOrDash(item.receiptAttachment, item.receipt_attachment),
    logisticsAttachment: strOrDash(item.logisticsAttachment, item.logistics_attachment),
    invoiceAttachment: strOrDash(item.invoiceAttachment, item.invoice_attachment),
  }))
}

function escapeCsvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`
  return v
}

function rowsToCsv(rows: DataRow[]): string {
  const headers = [
    '数据项ID',
    '数据名称',
    '来源',
    '类型',
    '单据状态',
    '上链状态',
    '合同号',
    '合同类型',
    '客户',
    '结算组织',
    '商品名称',
    '应收净额',
    '收款金额',
    '部分收款',
    '应收账款资产', // 暂时隐藏
    '已开票额',
    '合同创建日期',
    '合同收款日期',
    '状态',
    '创建时间',
  ]
  const lines = [headers.join(',')]
  for (const r of rows) {
    lines.push(
      [
        r.id,
        r.name,
        r.source,
        r.type,
        r.documentStatus,
        r.onChain,
        r.contractNo,
        r.contractType,
        r.customer,
        r.settlementOrg,
        r.productName,
        r.priceTaxTotal == null ? '-' : String(r.priceTaxTotal),
        r.receiptAmount == null ? '-' : String(r.receiptAmount),
        r.partialReceipt,
        r.unreceivedAmount == null ? '-' : String(r.unreceivedAmount), // 暂时隐藏
        r.invoicedAmount == null ? '-' : String(r.invoicedAmount),
        r.contractCreatedDate,
        r.contractReceiptDate,
        statusTextMap[r.status] ?? r.status,
        r.createdTime,
      ]
        .map((c) => escapeCsvCell(String(c ?? '')))
        .join(',')
    )
  }
  return '\uFEFF' + lines.join('\n')
}

function triggerDownloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function parseDayRangeTuple(t: [string | null, string | null] | null): [Dayjs | null, Dayjs | null] | null {
  if (!t?.[0] || !t?.[1]) return null
  const a = dayjs(t[0])
  const b = dayjs(t[1])
  return a.isValid() && b.isValid() ? [a, b] : null
}

function formatRangeTuple(r: [Dayjs | null, Dayjs | null] | null): [string | null, string | null] | null {
  if (!r?.[0] || !r?.[1]) return null
  return [r[0].format('YYYY-MM-DD'), r[1].format('YYYY-MM-DD')]
}

function useListCacheBootstrap() {
  const bootRef = useRef<{ cache: DataListCachePayload | null; restored: boolean } | undefined>(undefined)
  if (!bootRef.current) {
    const cache = getPendingRestoreCache()
    bootRef.current = { cache, restored: !!cache }
  }
  return bootRef.current
}

const DataVisualization = () => {
  const { message, modal } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const { cache: listCache, restored: listCacheRestored } = useListCacheBootstrap()
  const [activeTab, setActiveTab] = useState('table')
  const [assetType, setAssetType] = useState(listCache?.assetType ?? listCache?.appliedAssetType ?? 'all')
  const [assetStatus, setAssetStatus] = useState(listCache?.assetStatus ?? listCache?.appliedAssetStatus ?? 'all')
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    parseCachedDayRange(listCache?.appliedDateRange ?? null)
  )
  const [appliedAssetType, setAppliedAssetType] = useState(listCache?.appliedAssetType ?? 'all')
  const [appliedAssetStatus, setAppliedAssetStatus] = useState(listCache?.appliedAssetStatus ?? 'all')
  const [appliedDateRange, setAppliedDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    parseCachedDayRange(listCache?.appliedDateRange ?? null)
  )
  const [appliedKeyword, setAppliedKeyword] = useState(listCache?.appliedKeyword ?? '')
  const [keywordInput, setKeywordInput] = useState(listCache?.appliedKeyword ?? '')
  const [filterSource, setFilterSource] = useState<string[]>(() => resolveInitialSourceFilter(listCache))
  const [appliedSource, setAppliedSource] = useState<string[]>(() => resolveInitialSourceFilter(listCache))
  const [filterCustomer, setFilterCustomer] = useState<string[]>(normalizeStringList(listCache?.appliedCustomer))
  const [appliedCustomer, setAppliedCustomer] = useState<string[]>(normalizeStringList(listCache?.appliedCustomer))
  const [filterSettlementOrg, setFilterSettlementOrg] = useState<string[]>(
    normalizeStringList(listCache?.appliedSettlementOrg)
  )
  const [appliedSettlementOrg, setAppliedSettlementOrg] = useState<string[]>(
    normalizeStringList(listCache?.appliedSettlementOrg)
  )
  const [filterProductName, setFilterProductName] = useState<string[]>(
    normalizeStringList(listCache?.appliedProductName)
  )
  const [appliedProductName, setAppliedProductName] = useState<string[]>(
    normalizeStringList(listCache?.appliedProductName)
  )
  const [contractAmountMin, setContractAmountMin] = useState<number | null>(listCache?.appliedContractMin ?? null)
  const [contractAmountMax, setContractAmountMax] = useState<number | null>(listCache?.appliedContractMax ?? null)
  const [appliedContractMin, setAppliedContractMin] = useState<number | null>(listCache?.appliedContractMin ?? null)
  const [appliedContractMax, setAppliedContractMax] = useState<number | null>(listCache?.appliedContractMax ?? null)
  const [contractCreatedRange, setContractCreatedRange] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    parseCachedDayRange(listCache?.appliedContractCreatedRange ?? null)
  )
  const [appliedContractCreatedRange, setAppliedContractCreatedRange] = useState<[Dayjs | null, Dayjs | null] | null>(
    () => parseCachedDayRange(listCache?.appliedContractCreatedRange ?? null)
  )
  const [receiptDateRange, setReceiptDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    parseCachedDayRange(listCache?.appliedReceiptDateRange ?? null)
  )
  const [appliedReceiptDateRange, setAppliedReceiptDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    parseCachedDayRange(listCache?.appliedReceiptDateRange ?? null)
  )
  const [filterPartialReceipt, setFilterPartialReceipt] = useState<'all' | '是' | '否'>(
    listCache?.appliedPartialReceipt ?? 'all'
  )
  const [appliedPartialReceipt, setAppliedPartialReceipt] = useState<'all' | '是' | '否'>(
    listCache?.appliedPartialReceipt ?? 'all'
  )
  const [filterRwaCategory, setFilterRwaCategory] = useState<string[]>(listCache?.appliedRwaCategory ?? [])
  const [appliedRwaCategory, setAppliedRwaCategory] = useState<string[]>(listCache?.appliedRwaCategory ?? [])
  const [tableSort, setTableSort] = useState<{
    columnKey: string
    order: 'ascend' | 'descend'
  }>(listCache?.tableSort ?? { columnKey: 'contractCreatedDate', order: 'descend' })
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() => loadVisibleColumnKeys())
  const [columnOrder, setColumnOrder] = useState<string[]>(() => loadColumnOrder())
  const [columnModalOpen, setColumnModalOpen] = useState(false)
  const dragColumnKeyRef = useRef<string | null>(null)
  const [dragOverColumnKey, setDragOverColumnKey] = useState<string | null>(null)
  const [appliedAdvancedQuery, setAppliedAdvancedQuery] = useState<AdvancedQueryPayload>(
    () => listCache?.appliedAdvancedQuery ?? emptyAdvancedQuery()
  )
  const [advancedModalInitialQuery, setAdvancedModalInitialQuery] = useState<AdvancedQueryPayload>(
    () => listCache?.appliedAdvancedQuery ?? emptyAdvancedQuery()
  )
  const [advancedModalOpen, setAdvancedModalOpen] = useState(false)
  const [savedSchemes, setSavedSchemes] = useState<SavedFilterScheme[]>([])

  const [tableData, setTableData] = useState<DataRow[]>(() => (listCache?.tableData as DataRow[]) ?? [])
  const [listTotal, setListTotal] = useState(listCache?.listTotal ?? 0)
  const [listPage, setListPage] = useState(listCache?.listPage ?? 1)
  const [listPageSize, setListPageSize] = useState(listCache?.listPageSize ?? 10)
  const [dataLoading, setDataLoading] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryStats, setSummaryStats] = useState<{
    totalValue: number
    totalUnreceivedAmount: number
    totalReceiptAmount: number
    totalCount: number
    availableCount: number
  } | null>(listCache?.summaryStats ?? null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(listCache?.selectedRowKeys ?? [])
  const [exporting, setExporting] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [pendingExportMode, setPendingExportMode] = useState<'filtered' | 'selected'>('filtered')
  const [importing, setImporting] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importFileList, setImportFileList] = useState<UploadFile[]>([])
  const [outstockModalOpen, setOutstockModalOpen] = useState(false)
  const [outstockLoading, setOutstockLoading] = useState(false)
  const [outstockRows, setOutstockRows] = useState<Record<string, unknown>[]>([])
  const listRestoredFromCacheRef = useRef(listCacheRestored)

  const clearListCacheRestore = useCallback(() => {
    listRestoredFromCacheRef.current = false
    clearListCachePendingRestore()
  }, [])

  const listFiltersPayload = useMemo(() => {
    const filters: Record<string, unknown> = {}
    if (appliedAssetType !== 'all') filters.type = appliedAssetType
    if (appliedAssetStatus !== 'all') filters.status = appliedAssetStatus
    if (appliedKeyword.trim()) filters.keyword = appliedKeyword.trim()
    if (appliedDateRange?.[0] && appliedDateRange?.[1]) {
      filters.dateFrom = appliedDateRange[0].format('YYYY-MM-DD')
      filters.dateTo = appliedDateRange[1].format('YYYY-MM-DD')
    }
    if (appliedSource.length > 0) filters.source = appliedSource
    if (appliedCustomer.length > 0) filters.customer = appliedCustomer
    if (appliedSettlementOrg.length > 0) filters.settlementOrg = appliedSettlementOrg
    if (appliedProductName.length > 0) filters.productName = appliedProductName
    if (appliedContractMin != null) filters.contractTotalAmountMin = appliedContractMin
    if (appliedContractMax != null) filters.contractTotalAmountMax = appliedContractMax
    if (appliedContractCreatedRange?.[0] && appliedContractCreatedRange?.[1]) {
      filters.contractCreatedFrom = appliedContractCreatedRange[0].format('YYYY-MM-DD')
      filters.contractCreatedTo = appliedContractCreatedRange[1].format('YYYY-MM-DD')
    }
    if (appliedReceiptDateRange?.[0] && appliedReceiptDateRange?.[1]) {
      filters.receiptDateFrom = appliedReceiptDateRange[0].format('YYYY-MM-DD')
      filters.receiptDateTo = appliedReceiptDateRange[1].format('YYYY-MM-DD')
    }
    if (appliedPartialReceipt !== 'all') filters.partialReceipt = appliedPartialReceipt
    if (appliedRwaCategory.length > 0) filters.rwaCategory = appliedRwaCategory
    const hasAdvanced = appliedAdvancedQuery.groups.some((g) =>
      g.rows.some((r) => r.field && r.operator && String(r.value ?? '').trim() !== '')
    )
    if (hasAdvanced) filters.advancedQuery = appliedAdvancedQuery
    return filters
  }, [
    appliedAssetType,
    appliedAssetStatus,
    appliedKeyword,
    appliedDateRange,
    appliedSource,
    appliedCustomer,
    appliedSettlementOrg,
    appliedProductName,
    appliedContractMin,
    appliedContractMax,
    appliedContractCreatedRange,
    appliedReceiptDateRange,
    appliedPartialReceipt,
    appliedRwaCategory,
    appliedAdvancedQuery,
  ])

  const buildSnapshot = (): FilterFormSnapshot => ({
    assetType,
    assetStatus,
    keyword: keywordInput,
    filterSource,
    filterCustomer,
    filterSettlementOrg,
    filterProductName,
    contractAmountMin,
    contractAmountMax,
    contractCreatedRange: formatRangeTuple(contractCreatedRange),
    receiptDateRange: formatRangeTuple(receiptDateRange),
    dateRange: formatRangeTuple(dateRange),
    filterPartialReceipt,
    filterRwaCategory,
  })

  const applySnapshot = (s: FilterFormSnapshot) => {
    setAssetType(s.assetType)
    setAssetStatus(s.assetStatus)
    setKeywordInput(s.keyword)
    setFilterSource(normalizeStringList(s.filterSource))
    setFilterCustomer(normalizeStringList(s.filterCustomer))
    setFilterSettlementOrg(normalizeStringList(s.filterSettlementOrg))
    setFilterProductName(normalizeStringList(s.filterProductName))
    setContractAmountMin(s.contractAmountMin)
    setContractAmountMax(s.contractAmountMax)
    setContractCreatedRange(parseDayRangeTuple(s.contractCreatedRange))
    setReceiptDateRange(parseDayRangeTuple(s.receiptDateRange))
    setDateRange(parseDayRangeTuple(s.dateRange))
    setAppliedAssetType(s.assetType)
    setAppliedAssetStatus(s.assetStatus)
    setAppliedKeyword(s.keyword.trim())
    setAppliedSource(normalizeStringList(s.filterSource))
    setAppliedCustomer(normalizeStringList(s.filterCustomer))
    setAppliedSettlementOrg(normalizeStringList(s.filterSettlementOrg))
    setAppliedProductName(normalizeStringList(s.filterProductName))
    setAppliedContractMin(s.contractAmountMin)
    setAppliedContractMax(s.contractAmountMax)
    setAppliedContractCreatedRange(parseDayRangeTuple(s.contractCreatedRange))
    setAppliedReceiptDateRange(parseDayRangeTuple(s.receiptDateRange))
    setAppliedDateRange(parseDayRangeTuple(s.dateRange))
    const partial = s.filterPartialReceipt ?? 'all'
    const rwa = normalizeStringList(s.filterRwaCategory)
    setFilterPartialReceipt(partial)
    setFilterRwaCategory(rwa)
    setAppliedPartialReceipt(partial)
    setAppliedRwaCategory(rwa)
  }

  const refreshSavedSchemes = useCallback(async () => {
    try {
      const res = (await dataSourceFilterSchemesApi.list({})) as {
        data?: { items?: SavedFilterScheme[] }
        items?: SavedFilterScheme[]
      }
      const items = res?.data?.items ?? res?.items
      if (Array.isArray(items)) {
        setSavedSchemes(items as SavedFilterScheme[])
        return
      }
      setSavedSchemes([])
    } catch {
      setSavedSchemes([])
    }
  }, [])

  useEffect(() => {
    void refreshSavedSchemes()
  }, [refreshSavedSchemes])

  const fetchSummary = useCallback(() => {
    setSummaryLoading(true)
    const filters = { ...listFiltersPayload }
    saveSummaryFilters(filters)
    const body: Record<string, unknown> = {
      filters,
    }
    dataSourcesApi
      .summary(body)
      .then((res: any) => {
        const d = res?.data
        if (d && (typeof d.totalValue === 'number' || typeof d.totalCount === 'number')) {
          const clamp = (n: unknown) => {
            const x = Number(n ?? 0)
            return Number.isFinite(x) && x >= 0 ? x : 0
          }
          setSummaryStats({
            totalValue: clamp(d.totalValue ?? clamp(d.totalUnreceivedAmount) + clamp(d.totalReceiptAmount)),
            totalUnreceivedAmount: clamp(d.totalUnreceivedAmount),
            totalReceiptAmount: clamp(d.totalReceiptAmount),
            totalCount: clamp(d.totalCount),
            availableCount: clamp(d.availableCount ?? d.activeCount),
          })
        } else {
          setSummaryStats(null)
        }
      })
      .catch(() => setSummaryStats(null))
      .finally(() => setSummaryLoading(false))
  }, [listFiltersPayload])

  const fetchList = useCallback(() => {
    setDataLoading(true)
    const filters = { ...listFiltersPayload }
    const body: Record<string, unknown> = {
      page: listPage,
      pageSize: listPageSize,
      sort: {
        field: tableSort.columnKey,
        order: tableSort.order === 'ascend' ? 'asc' : 'desc',
      },
    }
    if (Object.keys(filters).length) body.filters = filters
    dataSourcesApi
      .list(body)
      .then((res: any) => {
        const rows = normalizeDataList(res)
        const data = res?.data
        setTableData(rows.length ? rows : [])
        setListTotal(Number(data?.total ?? data?.totalCount ?? rows.length))
      })
      .catch(() => {
        setTableData([])
        setListTotal(0)
      })
      .finally(() => setDataLoading(false))
  }, [listPage, listPageSize, listFiltersPayload, tableSort.columnKey, tableSort.order])

  useEffect(() => {
    if (listRestoredFromCacheRef.current) return
    fetchList()
  }, [fetchList])

  useEffect(() => {
    if (listRestoredFromCacheRef.current) return
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    const state = location.state as { searchKeyword?: string; highlightId?: string } | null
    const kw = state?.searchKeyword?.trim()
    const highlightId = state?.highlightId
    if (highlightId) {
      navigate(`/data/${encodeURIComponent(highlightId)}`, { replace: true, state: {} })
      return
    }
    if (kw) {
      clearListCacheRestore()
      setAppliedKeyword(kw)
      setKeywordInput(kw)
      setListPage(1)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate, clearListCacheRestore])

  const displayData = tableData

  const handleRefresh = () => {
    clearListCacheRestore()
    setAssetType('all')
    setAssetStatus('all')
    setDateRange(null)
    setFilterSource([])
    setFilterCustomer([])
    setFilterSettlementOrg([])
    setFilterProductName([])
    setContractAmountMin(null)
    setContractAmountMax(null)
    setContractCreatedRange(null)
    setReceiptDateRange(null)
    setFilterPartialReceipt('all')
    setAppliedPartialReceipt('all')
    setFilterRwaCategory([])
    setAppliedRwaCategory([])
    setTableSort({ columnKey: 'contractCreatedDate', order: 'descend' })
    setAppliedAssetType('all')
    setAppliedAssetStatus('all')
    setAppliedDateRange(null)
    setAppliedKeyword('')
    setKeywordInput('')
    setAppliedSource([])
    setAppliedCustomer([])
    setAppliedSettlementOrg([])
    setAppliedProductName([])
    setAppliedContractMin(null)
    setAppliedContractMax(null)
    setAppliedContractCreatedRange(null)
    setAppliedReceiptDateRange(null)
    setAppliedAdvancedQuery(emptyAdvancedQuery())
    setAdvancedModalInitialQuery(emptyAdvancedQuery())
    setSelectedRowKeys([])
    setListPage(1)
    message.success('数据已刷新')
  }

  const handleFilter = () => {
    clearListCacheRestore()
    const kw = keywordInput.trim()
    // 场景卡片里的类型/状态/日期在页面上未展示，清空以免沿用常用筛选场景里的隐含条件
    setAssetType('all')
    setAssetStatus('all')
    setDateRange(null)
    setAppliedAssetType('all')
    setAppliedAssetStatus('all')
    setAppliedDateRange(null)
    setAppliedKeyword(kw)
    setAppliedSource([...filterSource])
    setAppliedCustomer([...filterCustomer])
    setAppliedSettlementOrg([...filterSettlementOrg])
    setAppliedProductName([...filterProductName])
    setAppliedContractMin(contractAmountMin)
    setAppliedContractMax(contractAmountMax)
    setAppliedContractCreatedRange(contractCreatedRange)
    setAppliedReceiptDateRange(receiptDateRange)
    setAppliedPartialReceipt(filterPartialReceipt)
    setAppliedRwaCategory(filterRwaCategory)
    setListPage(1)
    message.success('已应用筛选条件')
  }

  const handleResetFilters = () => {
    clearListCacheRestore()
    setFilterSource([])
    setFilterCustomer([])
    setFilterSettlementOrg([])
    setFilterProductName([])
    setContractAmountMin(null)
    setContractAmountMax(null)
    setContractCreatedRange(null)
    setReceiptDateRange(null)
    setFilterPartialReceipt('all')
    setFilterRwaCategory([])
    setAssetType('all')
    setAssetStatus('all')
    setDateRange(null)
    setKeywordInput('')
    setAppliedSource([])
    setAppliedCustomer([])
    setAppliedSettlementOrg([])
    setAppliedProductName([])
    setAppliedContractMin(null)
    setAppliedContractMax(null)
    setAppliedContractCreatedRange(null)
    setAppliedReceiptDateRange(null)
    setAppliedPartialReceipt('all')
    setAppliedRwaCategory([])
    setAppliedAssetType('all')
    setAppliedAssetStatus('all')
    setAppliedDateRange(null)
    setAppliedKeyword('')
    setAppliedAdvancedQuery(emptyAdvancedQuery())
    setAdvancedModalInitialQuery(emptyAdvancedQuery())
    setListPage(1)
    message.success('筛选条件已重置')
  }

  const openAdvancedQueryModal = () => {
    setAdvancedModalInitialQuery(JSON.parse(JSON.stringify(appliedAdvancedQuery)) as AdvancedQueryPayload)
    setAdvancedModalOpen(true)
  }

  const handleAdvancedQueryApply = (query: AdvancedQueryPayload) => {
    clearListCacheRestore()
    setAppliedAdvancedQuery(JSON.parse(JSON.stringify(query)) as AdvancedQueryPayload)
    setListPage(1)
  }

  const handleSaveSchemeFromModal = async (name: string, query: AdvancedQueryPayload) => {
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `scheme_${Date.now()}`
    try {
      await dataSourceFilterSchemesApi.save({
        id,
        name,
        snapshot: buildSnapshot() as unknown as Record<string, unknown>,
        advancedQuery: JSON.parse(JSON.stringify(query)) as Record<string, unknown>,
      })
      await refreshSavedSchemes()
      message.success('已保存到服务端')
    } catch {
      message.error('保存场景失败，请稍后重试')
    }
  }

  const handleLoadSchemeFromModal = (scheme: SavedFilterScheme) => {
    clearListCacheRestore()
    applySnapshot(scheme.snapshot)
    setAppliedAdvancedQuery(JSON.parse(JSON.stringify(scheme.advancedQuery)) as AdvancedQueryPayload)
    setListPage(1)
    message.success(`已载入方案「${scheme.name}」`)
  }

  const handleApplySavedSchemeTag = (scheme: SavedFilterScheme) => {
    clearListCacheRestore()
    applySnapshot(scheme.snapshot)
    setAppliedAdvancedQuery(JSON.parse(JSON.stringify(scheme.advancedQuery)) as AdvancedQueryPayload)
    setListPage(1)
    message.success(`已应用场景「${scheme.name}」`)
  }

  const handleRemoveSavedScheme = async (id: string) => {
    try {
      await dataSourceFilterSchemesApi.delete({ id })
      await refreshSavedSchemes()
      message.success('已删除该场景')
    } catch {
      message.error('删除失败，请稍后重试')
    }
  }

  const handleOpenChain = (record: DataRow) => {
    const ordernumber = String(record.ordernumber ?? '').trim()
    const bill_no = String(record.bill_no ?? '').trim()
    if (!ordernumber || !bill_no) {
      message.warning('缺少订单号(ordernumber)或合同单号(bill_no)，无法提交上链')
      return
    }
    modal.confirm({
      title: '上链确认',
      content: `确认将数据项「${record.name}」（${record.id}）上链？提交后请等待链上确认。`,
      okText: '确认上链',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dataSourcesApi.contractChainUpload({ ordernumber, bill_no })
          message.success('已提交上链请求')
          fetchList()
          fetchSummary()
        } catch {
          message.error('上链请求失败')
          return Promise.reject(new Error('upload failed'))
        }
      },
    })
  }

  const handleDelete = (key: string) => {
    const record = tableData.find((item) => item.key === key)
    const id = record?.id ?? key
    dataSourcesApi
      .delete({ id })
      .then(() => {
        setTableData((prev) => prev.filter((item) => item.key !== key))
        message.success('数据项已删除')
      })
      .catch(() => {
        message.error('删除失败，请重试')
      })
  }

  const persistListCache = () => {
    markListCachePendingRestore()
    saveDataListCache({
      tableData,
      listTotal,
      listPage,
      listPageSize,
      selectedRowKeys,
      appliedAssetType,
      appliedAssetStatus,
      appliedKeyword,
      appliedSource,
      appliedCustomer,
      appliedSettlementOrg,
      appliedProductName,
      appliedContractMin,
      appliedContractMax,
      appliedContractCreatedRange: formatRangeTuple(appliedContractCreatedRange),
      appliedReceiptDateRange: formatRangeTuple(appliedReceiptDateRange),
      appliedDateRange: formatRangeTuple(appliedDateRange),
      appliedAdvancedQuery,
      appliedPartialReceipt,
      appliedRwaCategory,
      assetType,
      assetStatus,
      tableSort,
      summaryStats,
    })
  }

  const handleView = (record: DataRow) => {
    persistListCache()
    navigate(`/data/${encodeURIComponent(record.id)}`)
  }

  const handleOutstockDetail = async (record: DataRow) => {
    setOutstockModalOpen(true)
    setOutstockRows([])
    if (!record.ordernumber) {
      message.warning('缺少销售订单号，无法查询出库明细')
      return
    }
    setOutstockLoading(true)
    try {
      const res = await dataSourcesApi.kingdeeTabSalesOutstock({
        id: record.id,
        ordernumber: record.ordernumber,
        bill_no: record.bill_no || record.ordernumber,
      })
      setOutstockRows(unwrapKingdeeTabRows(res))
    } catch {
      message.error('出库明细加载失败，请稍后重试')
    } finally {
      setOutstockLoading(false)
    }
  }

  const selectedRows = useMemo(
    () => tableData.filter((r) => selectedRowKeys.includes(r.key)),
    [tableData, selectedRowKeys]
  )

  const downloadImportTemplate = async () => {
    try {
      const res = await dataSourcesApi.exportTemplate()
      if (res instanceof Blob) {
        triggerDownloadBlob(res, '资产数据导入模板.csv')
        message.success('模板已下载')
        return
      }
      message.error('模板下载失败')
    } catch {
      message.error('模板下载失败，请稍后重试')
    }
  }

  const pollImportStatus = async (importId: string, maxAttempts = 60): Promise<boolean> => {
    for (let i = 0; i < maxAttempts; i += 1) {
      await new Promise((r) => setTimeout(r, 2000))
      try {
        const res = (await importLogsApi.status({ importId })) as {
          data?: { status?: string; inserted?: number; updated?: number; failed?: number }
        }
        const st = res?.data?.status
        if (!st || st === 'pending' || st === 'processing') continue
        const inserted = res?.data?.inserted ?? 0
        const updated = res?.data?.updated ?? 0
        const failed = res?.data?.failed ?? 0
        if (st === 'failure') {
          message.error(`导入失败，请在「导入日志」中下载失败明细`)
        } else if (st === 'partial') {
          message.warning(`导入部分成功：新增 ${inserted} 条，更新 ${updated} 条，失败 ${failed} 条`)
        } else {
          message.success(`导入完成：新增 ${inserted} 条，更新 ${updated} 条`)
        }
        return true
      } catch {
        return false
      }
    }
    message.info('导入任务仍在处理中，请到「导入日志」查看进度')
    return false
  }

  const resolveImportFile = (list: UploadFile[]): File | undefined => {
    const item = list[0]
    if (!item) return undefined
    if (item.originFileObj instanceof File) return item.originFileObj
    return item as unknown as File
  }

  const handleImportSubmit = async () => {
    const file = resolveImportFile(importFileList)
    if (!file) {
      message.warning('请先选择 CSV 文件')
      return
    }
    setImporting(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = (await dataSourcesApi.import(formData)) as {
        message?: string
        data?: { importId?: string; status?: string }
      }
      const importId = res?.data?.importId
      message.success(res?.message ?? '导入任务已提交')
      setImportModalOpen(false)
      setImportFileList([])
      if (importId) {
        const done = await pollImportStatus(importId)
        if (done) {
          fetchList()
          fetchSummary()
        }
      }
    } catch (err: unknown) {
      const msg = (err as { friendlyMessage?: string })?.friendlyMessage ?? '导入失败，请检查文件格式'
      message.error(msg)
    } finally {
      setImporting(false)
    }
  }

  const openExportModal = (mode: 'filtered' | 'selected') => {
    if (mode === 'selected' && !selectedRows.length) {
      message.warning('请先勾选要导出的数据行')
      return
    }
    setPendingExportMode(mode)
    setExportModalOpen(true)
  }

  const runExport = async (mode: 'filtered' | 'selected', columns: string[]) => {
    if (mode === 'selected' && !selectedRows.length) {
      message.warning('请先勾选要导出的数据行')
      return
    }
    setExporting(true)
    try {
      const filters = { ...listFiltersPayload }
      const body: Record<string, unknown> = {
        mode,
        format: 'csv',
        filters,
        columns,
      }
      if (mode === 'selected') {
        body.ids = selectedRows.map((r) => r.id).filter(Boolean)
      }
      try {
        const res: unknown = await dataSourcesApi.export(body)
        if (res instanceof Blob) {
          if (res.type.includes('json')) {
            const text = await res.text()
            try {
              const j = JSON.parse(text) as { data?: { fileUrl?: string }; fileUrl?: string }
              const url = j?.data?.fileUrl ?? j?.fileUrl
              if (url) {
                window.open(url, '_blank')
                message.success('已打开导出文件链接')
                return
              }
            } catch {
              /* fall through */
            }
          } else {
            triggerDownloadBlob(res, `资产数据导出_${dayjs().format('YYYYMMDD_HHmmss')}.csv`)
            message.success('导出成功')
            return
          }
        }
      } catch {
        /* client fallback */
      }
      const rows = mode === 'selected' ? selectedRows : displayData
      if (!rows.length) {
        message.warning('当前没有可导出的数据')
        return
      }
      const csv = rowsToCsv(rows)
      triggerDownloadBlob(
        new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
        `资产数据导出_${dayjs().format('YYYYMMDD_HHmmss')}.csv`
      )
      message.success('已使用本地导出（接口不可用或返回非文件时）')
    } finally {
      setExporting(false)
    }
  }

  const money = (n: number) => `¥${Number(n ?? 0).toLocaleString()}`
  const moneyCell = (v: number | null | undefined) => {
    if (v == null) return '-'
    const n = Number(v)
    if (!Number.isFinite(n)) return '-'
    return money(n)
  }

  /** 合同/订单/收款/物流/发票附件：完整 | 部分 | 缺失（兼容历史 有/没有） */
  const attachmentTag = (v: string) => {
    if (!v || v === '-') return '-'
    const colorMap: Record<string, string> = {
      完整: 'green',
      部分: 'orange',
      缺失: 'red',
      有: 'green',
      没有: 'default',
    }
    return <Tag color={colorMap[v] ?? 'default'}>{v}</Tag>
  }

  const handleColumnSort = useCallback((key: string) => {
    clearListCacheRestore()
    setTableSort((prev) =>
      prev.columnKey === key
        ? { columnKey: key, order: prev.order === 'ascend' ? 'descend' : 'ascend' }
        : { columnKey: key, order: 'ascend' }
    )
    setListPage(1)
  }, [clearListCacheRestore])

  const buildHeaderTitle = useCallback(
    (key: string, label: ReactNode) => (
      <DataListColumnHeader
        columnKey={key}
        label={label}
        sortable={LIST_SORTABLE_KEYS.has(key)}
        sortColumnKey={tableSort.columnKey}
        sortOrder={tableSort.order}
        onSort={handleColumnSort}
        showDragHandle={key !== 'action'}
        onDragStart={(e) => {
          dragColumnKeyRef.current = key
          e.dataTransfer.setData('text/plain', key)
          e.dataTransfer.effectAllowed = 'move'
        }}
        onDragEnd={() => {
          dragColumnKeyRef.current = null
          setDragOverColumnKey(null)
        }}
      />
    ),
    [tableSort.columnKey, tableSort.order, handleColumnSort]
  )

  const allColumns = useMemo(() => {
    const withHeader = (key: string, col: Record<string, unknown>) => {
      const label = col.title
      const text = typeof label === 'string' ? label : key
      return { ...col, key, title: buildHeaderTitle(key, text), align: 'center' as const }
    }

    return [
      withHeader('contractAttachment', {
        title: '合同附件',
        dataIndex: 'contractAttachment',
        key: 'contractAttachment',
        width: 90,
        render: (v: string) => attachmentTag(v),
      }),
      withHeader('orderAttachment', {
        title: '订单附件',
        dataIndex: 'orderAttachment',
        key: 'orderAttachment',
        width: 90,
        render: (v: string) => attachmentTag(v),
      }),
      withHeader('receiptAttachment', {
        title: '收款附件',
        dataIndex: 'receiptAttachment',
        key: 'receiptAttachment',
        width: 90,
        render: (v: string) => attachmentTag(v),
      }),
      withHeader('logisticsAttachment', {
        title: '物流附件',
        dataIndex: 'logisticsAttachment',
        key: 'logisticsAttachment',
        width: 90,
        render: (v: string) => attachmentTag(v),
      }),
      withHeader('invoiceAttachment', {
        title: '发票附件',
        dataIndex: 'invoiceAttachment',
        key: 'invoiceAttachment',
        width: 90,
        render: (v: string) => attachmentTag(v),
      }),
      withHeader('id', { title: '数据项ID', dataIndex: 'id', key: 'id', width: 120, ellipsis: true }),
      withHeader('name', { title: '数据名称', dataIndex: 'name', key: 'name', width: 160, ellipsis: true }),
      withHeader('source', { title: '来源', dataIndex: 'source', key: 'source', width: 100, ellipsis: true }),
      withHeader('type', {
        title: '类型',
        dataIndex: 'type',
        key: 'type',
        width: 90,
        render: (t: string) => <Tag color="blue">{t}</Tag>,
      }),
      withHeader('documentStatus', {
        title: '单据状态',
        dataIndex: 'documentStatus',
        key: 'documentStatus',
        width: 130,
        ellipsis: true,
      }),
      withHeader('onChain', {
        title: '上链状态',
        dataIndex: 'onChain',
        key: 'onChain',
        width: 120,
        render: (v: string) => {
          if (v === '已上链') return <Tag color="green">已上链</Tag>
          if (v === '未上链') return <Tag color="default">未上链</Tag>
          return '-'
        },
      }),
      withHeader('contractNo', { title: '合同号', dataIndex: 'contractNo', key: 'contractNo', width: 120, ellipsis: true }),
      withHeader('contractType', { title: '合同类型', dataIndex: 'contractType', key: 'contractType', width: 120, ellipsis: true }),
      withHeader('customer', { title: '客户', dataIndex: 'customer', key: 'customer', width: 120, ellipsis: true }),
      withHeader('settlementOrg', { title: '结算组织', dataIndex: 'settlementOrg', key: 'settlementOrg', width: 120, ellipsis: true }),
      withHeader('productName', { title: '商品名称', dataIndex: 'productName', key: 'productName', width: 140, ellipsis: true }),
      withHeader('rwaCategory', {
        title: '业务分类',
        dataIndex: 'rwaCategory',
        key: 'rwaCategory',
        width: 120,
        render: (v: string) => (v && v !== '-' ? <Tag>{v}</Tag> : '-'),
      }),
      withHeader('priceTaxTotal', {
        title: '应收净额',
        dataIndex: 'priceTaxTotal',
        key: 'priceTaxTotal',
        width: 110,
        render: (_: unknown, row: DataRow) => moneyCell(row.priceTaxTotal),
      }),
      withHeader('receiptAmount', {
        title: '收款金额',
        dataIndex: 'receiptAmount',
        key: 'receiptAmount',
        width: 110,
        render: (_: unknown, row: DataRow) => moneyCell(row.receiptAmount),
      }),
      withHeader('partialReceipt', {
        title: '部分收款',
        dataIndex: 'partialReceipt',
        key: 'partialReceipt',
        width: 90,
        render: (v: string) => {
          if (!v || v === '-') return '-'
          return <Tag color={v === '是' ? 'orange' : 'default'}>{v}</Tag>
        },
      }),
      withHeader('unreceivedAmount', {
        title: '未收款金额',
        dataIndex: 'unreceivedAmount',
        key: 'unreceivedAmount',
        width: 110,
        render: (_: unknown, row: DataRow) => moneyCell(row.unreceivedAmount),
      }),
      withHeader('invoicedAmount', {
        title: '已开票额',
        dataIndex: 'invoicedAmount',
        key: 'invoicedAmount',
        width: 110,
        render: (_: unknown, row: DataRow) => moneyCell(row.invoicedAmount),
      }),
      withHeader('contractCreatedDate', {
        title: '合同创建日期',
        dataIndex: 'contractCreatedDate',
        key: 'contractCreatedDate',
        width: 148,
        className: 'data-col-date',
      }),
      withHeader('contractReceiptDate', {
        title: '合同收款日期',
        dataIndex: 'contractReceiptDate',
        key: 'contractReceiptDate',
        width: 148,
        className: 'data-col-date',
      }),
      withHeader('status', {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (status: string) => <Tag color={statusColorMap[status]}>{statusTextMap[status] ?? status}</Tag>,
      }),
      withHeader('createdTime', {
        title: '创建时间',
        dataIndex: 'createdTime',
        key: 'createdTime',
        width: 170,
        render: (t: string) => formatDisplayDateTime(t),
      }),
      {
        title: (
          <span className="data-list-col-header flex justify-center items-center gap-0.5 w-full">
            操作
            <Tooltip title="自定义列">
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                className="!px-1 !min-w-0"
                aria-label="自定义列"
                onClick={(e) => {
                  e.stopPropagation()
                  setColumnModalOpen(true)
                }}
              />
            </Tooltip>
          </span>
        ),
        key: 'action',
        align: 'center' as const,
        fixed: 'right' as const,
        width: 280,
        onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        render: (_: unknown, record: DataRow) => (
          <Space size={0} wrap={false} className="whitespace-nowrap">
            <Button type="link" size="small" onClick={() => handleView(record)}>
              查看
            </Button>
            <Button type="link" size="small" onClick={() => void handleOutstockDetail(record)}>
              出库明细
            </Button>
            {!record.onChainListed ? (
              <Button type="link" size="small" onClick={() => handleOpenChain(record)}>
                上链
              </Button>
            ) : null}
            <Permission code="data_sources:delete">
              <Popconfirm
                title="确认删除该数据项？"
                description="删除后将无法恢复，请确认已完成备份。"
                okText="删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDelete(record.key)}
              >
                <Button type="link" size="small" danger>
                  删除
                </Button>
              </Popconfirm>
            </Permission>
          </Space>
        ),
      },
    ]
  }, [buildHeaderTitle])

  const handleColumnReorder = useCallback((fromKey: string, toKey: string) => {
    if (fromKey === toKey || fromKey === 'action' || toKey === 'action') return
    setColumnOrder((prev) => {
      const next = reorderColumnKeys(prev, fromKey, toKey)
      saveColumnOrder(next)
      return next
    })
  }, [])

  const withColumnDrag = useCallback(
    (key: string, col: Record<string, unknown>) => {
      if (key === 'action') return col
      const colClass = typeof col.className === 'string' ? col.className : ''
      return {
        ...col,
        onHeaderCell: () => ({
          className: [
            colClass,
            'data-col-draggable',
            dragOverColumnKey === key ? 'data-col-drag-over' : '',
          ]
            .filter(Boolean)
            .join(' '),
          onDragEnter: (e: React.DragEvent<HTMLTableCellElement>) => {
            e.preventDefault()
            setDragOverColumnKey(key)
          },
          onDragOver: (e: React.DragEvent<HTMLTableCellElement>) => {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            setDragOverColumnKey(key)
          },
          onDragLeave: () => {
            setDragOverColumnKey((prev) => (prev === key ? null : prev))
          },
          onDrop: (e: React.DragEvent<HTMLTableCellElement>) => {
            e.preventDefault()
            const from = dragColumnKeyRef.current ?? e.dataTransfer.getData('text/plain')
            dragColumnKeyRef.current = null
            setDragOverColumnKey(null)
            if (!from || from === key) return
            handleColumnReorder(from, key)
          },
          onDragEnd: () => {
            dragColumnKeyRef.current = null
            setDragOverColumnKey(null)
          },
        }),
      }
    },
    [dragOverColumnKey, handleColumnReorder]
  )

  const visibleKeySet = useMemo(() => new Set(visibleColumnKeys), [visibleColumnKeys])
  const columns = useMemo(() => {
    const orderIndex = new Map(columnOrder.map((k, i) => [k, i]))
    const filtered = allColumns.filter((col) => {
      const key = String(col.key ?? '')
      if (key === 'action') return true
      return visibleKeySet.has(key)
    })
    const sorted = filtered.sort((a, b) => {
      const ak = String(a.key ?? '')
      const bk = String(b.key ?? '')
      if (ak === 'action') return 1
      if (bk === 'action') return -1
      return (orderIndex.get(ak) ?? 9999) - (orderIndex.get(bk) ?? 9999)
    })
    return sorted.map((col) => withColumnDrag(String(col.key ?? ''), col as Record<string, unknown>))
  }, [allColumns, visibleKeySet, columnOrder, withColumnDrag])

  const handleTableChange = (pagination: { current?: number; pageSize?: number }) => {
    clearListCacheRestore()
    if (pagination.current) setListPage(pagination.current)
    if (pagination.pageSize) setListPageSize(pagination.pageSize)
  }

  const exportMenuItems = [
    {
      key: 'filtered',
      label: '按当前筛选条件导出',
      onClick: () => openExportModal('filtered'),
    },
    {
      key: 'selected',
      label: '导出已选中数据',
      onClick: () => openExportModal('selected'),
    },
  ]

  const hasSummary = summaryStats != null
  const showVal = (val: number, formatter: (n: number) => string) => (hasSummary && val !== 0 ? formatter(val) : '')

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold mb-2">资产数据</h1>
          <p className="text-gray-600">统一管理与查看数字资产数据明细</p>
        </div>
        <Space wrap>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading={dataLoading}>
            刷新数据
          </Button>
        </Space>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">总资产价值</div>
          <div className="text-2xl font-semibold" style={{ color: '#3b82f6' }}>
            {showVal(summaryStats?.totalValue ?? 0, (n) => `¥${(n / 10000).toFixed(2)}万`)}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">未收款资产</div>
          <div className="text-2xl font-semibold" style={{ color: '#ef4444' }}>
            {showVal(summaryStats?.totalUnreceivedAmount ?? 0, (n) => `¥${(n / 10000).toFixed(2)}万`)}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">已收款资产</div>
          <div className="text-2xl font-semibold" style={{ color: '#10b981' }}>
            {showVal(summaryStats?.totalReceiptAmount ?? 0, (n) => `¥${(n / 10000).toFixed(2)}万`)}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">资产数量总量</div>
          <div className="text-2xl font-semibold" style={{ color: '#8b5cf6' }}>
            {showVal(summaryStats?.totalCount ?? 0, (n) => String(n))}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">可用资产数据总量</div>
          <div className="text-2xl font-semibold" style={{ color: '#f59e0b' }}>
            {showVal(summaryStats?.availableCount ?? 0, (n) => String(n))}
          </div>
        </Card>
      </div>

      <div className="flex flex-col gap-6">
      <Card title="筛选条件">
        <div className="space-y-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">来源</div>
              <FilterMultiSelect
                field="source"
                value={filterSource}
                onChange={setFilterSource}
                placeholder="全部"
                fixedOptions={DATA_SOURCE_FILTER_SOURCES}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">客户</div>
              <FilterMultiSelect
                field="customer"
                value={filterCustomer}
                onChange={setFilterCustomer}
                placeholder="全部"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">结算组织</div>
              <FilterMultiSelect
                field="settlementOrg"
                value={filterSettlementOrg}
                onChange={setFilterSettlementOrg}
                placeholder="全部"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">商品</div>
              <FilterMultiSelect
                field="productName"
                value={filterProductName}
                onChange={setFilterProductName}
                placeholder="全部"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">应收净额</div>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  style={{ width: '50%' }}
                  placeholder="最小"
                  min={0}
                  value={contractAmountMin ?? undefined}
                  onChange={(v) => setContractAmountMin(v == null ? null : Number(v))}
                />
                <InputNumber
                  style={{ width: '50%' }}
                  placeholder="最大"
                  min={0}
                  value={contractAmountMax ?? undefined}
                  onChange={(v) => setContractAmountMax(v == null ? null : Number(v))}
                />
              </Space.Compact>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">业务分类</div>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="全部"
                value={filterRwaCategory}
                onChange={(v) => setFilterRwaCategory(v)}
                options={RWA_CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                maxTagCount="responsive"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">部分收款</div>
              <Select
                style={{ width: '100%' }}
                value={filterPartialReceipt}
                onChange={(v) => setFilterPartialReceipt(v)}
                options={[
                  { value: 'all', label: '全部' },
                  { value: '是', label: '是' },
                  { value: '否', label: '否' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">合同创建时间</div>
              <RangePicker
                style={{ width: '100%' }}
                value={contractCreatedRange}
                onChange={(d) => setContractCreatedRange(d as [Dayjs | null, Dayjs | null] | null)}
              />
            </Col>
            {/* 与合同创建时间同栅格宽度 lg=6，两个 RangePicker 在 100% 下等宽；按钮单独一列避免挤压日期 */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">收款日期</div>
              <RangePicker
                style={{ width: '100%' }}
                value={receiptDateRange}
                onChange={(d) => setReceiptDateRange(d as [Dayjs | null, Dayjs | null] | null)}
              />
            </Col>
            <Col xs={24} className="data-filter-actions-col">
              <div className="data-filter-actions-buttons">
                <Button icon={<FilterOutlined />} type="primary" onClick={handleFilter}>
                  应用筛选
                </Button>
                <Button icon={<SearchOutlined />} onClick={openAdvancedQueryModal}>
                  高级查询
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleResetFilters}>
                  重置筛选
                </Button>
              </div>
            </Col>
          </Row>
          <div className="pt-2 mt-2">
            <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
              <span className="text-sm font-medium text-gray-700 shrink-0 leading-[22px]">常用筛选场景：</span>
              {savedSchemes.length > 0 ? (
                <Space wrap className="flex-1 min-w-0">
                  {savedSchemes.map((s) => (
                    <Tag
                      key={s.id}
                      className="cursor-pointer py-0.5 px-2 text-sm"
                      onClick={() => handleApplySavedSchemeTag(s)}
                      closable
                      onClose={() => handleRemoveSavedScheme(s.id)}
                    >
                      {s.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <span className="text-gray-400 text-sm leading-[22px]">暂无已保存场景，可在「高级查询」中保存方案</span>
              )}
            </div>
          </div>
          {/* 列表筛选模块暂时隐藏，仍保留状态供路由关键词等逻辑使用 */}
        </div>
      </Card>

      <Card
        title="资产数据列表"
        styles={{ body: { paddingTop: 12 } }}
        extra={
          <Space wrap>
            <Permission codes={['data:import', 'data_sources:import', 'data_sources:list']}>
              <Button icon={<DownloadOutlined />} onClick={() => void downloadImportTemplate()}>
                下载导入模板
              </Button>
            </Permission>
            <Permission codes={['data:import', 'data_sources:import', 'data_sources:list']}>
              <Button icon={<UploadOutlined />} onClick={() => setImportModalOpen(true)}>
                导入数据
              </Button>
            </Permission>
            <Permission codes={['data:export', 'data_sources:export', 'data_sources:list']}>
              <Dropdown menu={{ items: exportMenuItems }} disabled={exporting}>
                <Button type="primary" icon={<DownloadOutlined />} loading={exporting}>
                  导出数据 <DownOutlined />
                </Button>
              </Dropdown>
            </Permission>
          </Space>
        }
      >
        <Tabs
          size="small"
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarStyle={{ marginBottom: 8 }}
          items={[
            {
              key: 'table',
              label: (
                <span>
                  <TableOutlined />
                  表格视图
                </span>
              ),
              children: (
                <Table
                  className="data-source-list-table"
                  dataSource={displayData}
                  columns={columns}
                  loading={dataLoading}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                    preserveSelectedRowKeys: true,
                  }}
                  pagination={{
                    current: listPage,
                    pageSize: listPageSize,
                    total: listTotal,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (t) => `共 ${t} 条`,
                  }}
                  onChange={handleTableChange}
                  rowClassName="hover:bg-gray-50 cursor-pointer"
                  scroll={{ x: 3100 }}
                  onRow={(record) => ({
                    onDoubleClick: () => handleView(record),
                  })}
                />
              ),
            },
          ]}
        />
      </Card>
      </div>

      <AdvancedQueryModal
        open={advancedModalOpen}
        onClose={() => setAdvancedModalOpen(false)}
        initialQuery={advancedModalInitialQuery}
        onQuery={handleAdvancedQueryApply}
        schemes={savedSchemes}
        onSaveScheme={handleSaveSchemeFromModal}
        onLoadScheme={handleLoadSchemeFromModal}
      />
      <ExportFieldModal
        open={exportModalOpen}
        exporting={exporting}
        onCancel={() => setExportModalOpen(false)}
        onConfirm={(cols) => {
          setExportModalOpen(false)
          void runExport(pendingExportMode, cols)
        }}
      />
      <ColumnSettingsModal
        open={columnModalOpen}
        initialKeys={visibleColumnKeys}
        onCancel={() => setColumnModalOpen(false)}
        onConfirm={(keys) => {
          setVisibleColumnKeys(keys)
          saveVisibleColumnKeys(keys)
          setColumnOrder((prev) => {
            const keySet = new Set(keys)
            const next = normalizeColumnOrder([
              ...prev.filter((k) => keySet.has(k)),
              ...keys.filter((k) => !prev.includes(k)),
              ...prev.filter((k) => !keySet.has(k)),
            ])
            saveColumnOrder(next)
            return next
          })
          setColumnModalOpen(false)
          message.success('列表列设置已保存')
        }}
      />
      <OutstockDetailModal
        open={outstockModalOpen}
        loading={outstockLoading}
        rows={outstockRows}
        onClose={() => {
          setOutstockModalOpen(false)
          setOutstockRows([])
        }}
      />
      <Modal
        title="导入资产数据"
        open={importModalOpen}
        onCancel={() => {
          if (!importing) {
            setImportModalOpen(false)
            setImportFileList([])
          }
        }}
        onOk={() => void handleImportSubmit()}
        okText="开始导入"
        cancelText="取消"
        confirmLoading={importing}
        destroyOnClose
      >
        <p className="text-gray-600 mb-4">
          请先下载导入模板，按模板填写后上传 CSV 文件。数据项 ID 留空将自动生成；已存在的 ID 将更新对应记录。
          导入为异步处理，提交后可在「系统设置 → 日志管理 → 导入日志」查看进度；若有失败记录，可下载失败数据修正后重新导入。
        </p>
        <Upload
          accept=".csv"
          maxCount={1}
          fileList={importFileList}
          beforeUpload={(file) => {
            setImportFileList([
              {
                uid: file.uid,
                name: file.name,
                status: 'done',
                originFileObj: file,
              },
            ])
            return false
          }}
          onRemove={() => {
            setImportFileList([])
          }}
        >
          <Button icon={<UploadOutlined />}>选择 CSV 文件</Button>
        </Upload>
      </Modal>
    </div>
  )
}

export default DataVisualization
